import { logout } from "../functions/logout";
/**
 * Session Management Handler
 * überwacht den Session-Status und leitet bei Ablauf zum Login weiter
 */

class SessionHandler {
  constructor() {
    // Session-Konfiguration vom Server (in Minuten)
    this.sessionTimeoutMinutes = this.getSessionTimeoutFromServer();
    this.warningBeforeMinutes = 2;

    this.sessionTimer = null;
    this.warningTimer = null;
    this.lastActivityTime = Date.now();

    this.initializeFetchInterceptor();
    this.initializeXHRInterceptor();
    this.setupActivityTracking();
    this.startSessionMonitoring();
  }

  /**
   * Liest Session-Timeout aus Meta-Tag oder verwendet Default
   */
  getSessionTimeoutFromServer() {
    const metaTag = document.querySelector('meta[name="session-timeout"]');

    if (metaTag) {
      return parseInt(metaTag.content) || 1;
    }

    return 1;
  }

  /**
   * Startet Session-Monitoring mit Timern
   */
  startSessionMonitoring() {
    this.resetSessionTimer();
    console.log(`Session monitoring started. Timeout: ${this.sessionTimeoutMinutes} minutes`);
  }

  /**
   * Setzt Session-Timer zurück bei Aktivität
   */
  resetSessionTimer() {
    this.lastActivityTime = Date.now();

    // Lösche bestehende Timer
    if (this.sessionTimer) clearTimeout(this.sessionTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);

    // Warning-Timer
    const warningTime = (this.sessionTimeoutMinutes - this.warningBeforeMinutes) * 60 * 1000;
    if (warningTime > 0) {
      this.warningTimer = setTimeout(() => {
        this.checkSessionStatus();
      }, warningTime);
    }

    // Session-Timeout Timer
    const timeoutTime = this.sessionTimeoutMinutes * 60 * 1000;
    this.sessionTimer = setTimeout(() => {
      this.handleSessionTimeout();
    }, timeoutTime);
  }

  /**
   * Prüft, ob die Session im Backend (z.B. durch anderen Tab) noch aktiv ist
   */
  async checkSessionStatus() {
    try {
      const response = await fetch("/session/check", {
        method: "GET",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (response.ok) {
        const data = await response.json();
        const warningLimit = this.sessionTimeoutMinutes - this.warningBeforeMinutes;

        // Wenn die Session erst kürzlich (kürzer als das Warn-Limit) in einer anderen App/Tab genutzt wurde
        if (data.idleMinutes !== undefined && data.idleMinutes < warningLimit) {
          console.log(
            `Session still active elsewhere (idle for ${data.idleMinutes}m). Quietly resetting local timers.`,
          );
          this.resetSessionTimer();
        } else {
          // Wirklich global inaktiv -> Warnung anzeigen
          this.showWarning();
        }
      } else {
        // Session bereits abgelaufen
        this.handleSessionExpired();
      }
    } catch (error) {
      console.error("Session check error:", error);
      // Fallback: Warnung anzeigen, wenn Check fehlschlägt
      this.showWarning();
    }
  }

  /**
   * Zeigt Warnung vor Session-Ablauf
   */
  showWarning() {
    if (typeof cs.Swal !== "undefined") {
      cs.Swal.fire({
        icon: "warning",
        title: "Session läuft bald ab",
        text: `Ihre Session läuft in ${this.warningBeforeMinutes} Minute(n) ab. Möchten Sie eingeloggt bleiben?`,
        showCancelButton: true,
        confirmButtonText: "Ja, eingeloggt bleiben",
        cancelButtonText: "Ausloggen",
        timer: this.warningBeforeMinutes * 60 * 1000,
        timerProgressBar: true,
      }).then((result) => {
        if (result.isConfirmed) {
          // eingeloggt bleiben - Session verlängern
          this.pingServer();
        } else if (result.dismiss === cs.Swal.DismissReason.cancel) {
          // User möchte ausloggen
          logout();
        }
        // Bei Timer-Ablauf passiert nichts - handleSessionTimeout() wird aufgerufen
      });
    } else {
      const stayLoggedIn = confirm(
        `Ihre Session läuft in ${this.warningBeforeMinutes} Minute(n) ab. Möchten Sie eingeloggt bleiben?`,
      );
      if (stayLoggedIn) {
        this.pingServer();
      }
    }
  }

  /**
   * Behandelt Session-Timeout (wird nach Ablauf aufgerufen)
   */
  handleSessionTimeout() {
    console.log("Session timeout reached, logging out...");
    this.handleSessionExpired();
  }

  /**
   * Interceptor für Fetch API
   */
  initializeFetchInterceptor() {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      // Session-Timer bei jeder Anfrage zurücksetzen
      this.resetSessionTimer();

      try {
        const response = await originalFetch(...args);

        // Session-Ablauf prüfen
        if (response.status === 401 && response.headers.get("X-Session-Expired")) {
          this.handleSessionExpired();

          return new Response(null, { status: response.status, statusText: response.statusText });
        }

        return response;
      } catch (error) {
        throw error;
      }
    };
  }

  /**
   * Interceptor für XMLHttpRequest
   */
  initializeXHRInterceptor() {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (...args) {
      this._url = args[1];
      originalOpen.apply(this, args);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      // Session-Timer bei jeder Anfrage zurücksetzen
      window.sessionHandler?.resetSessionTimer();

      // X-Requested-With Header hinzufügen
      this.setRequestHeader("X-Requested-With", "XMLHttpRequest");

      this.addEventListener("load", function () {
        if (this.status === 401 && this.getResponseHeader("X-Session-Expired")) {
          window.sessionHandler?.handleSessionExpired();
        }
      });

      originalSend.apply(this, args);
    };
  }

  /**
   * Activity Tracking - Session bei User-Aktivität verlängern
   */
  setupActivityTracking() {
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];

    // Throttle für Performance
    let isThrottled = false;
    const throttleTime = 1000; // Max. 1x pro Sekunde

    const handleActivity = () => {
      if (isThrottled) return;

      isThrottled = true;
      this.resetSessionTimer();

      setTimeout(() => {
        isThrottled = false;
      }, throttleTime);
    };

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
  }

  /**
   * Server-Ping zur Session-Verlängerung
   */
  async pingServer() {
    const csrfHeader = document.querySelector('meta[name="_csrf_header"]')?.content;
    const csrfToken = document.querySelector('meta[name="_csrf"]')?.content;

    if (!csrfHeader || !csrfToken) {
      console.error("CSRF token not found for session ping.");
      this.handleSessionExpired();
      return;
    }

    try {
      const response = await fetch("/session/ping", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          [csrfHeader]: csrfToken,
        },
      });

      if (response.ok) {
        console.log("Session erfolgreich verlängert");
        this.resetSessionTimer();
      } else {
        console.warn("Session ping fehlgeschlagen");
        this.handleSessionExpired();
      }
    } catch (error) {
      console.error("Session ping error:", error);
      this.handleSessionExpired();
    }
  }

  /**
   * Session-Ablauf Handler
   */
  handleSessionExpired() {
    // Verhindere mehrfache Redirects
    if (this._isRedirecting) {
      return;
    }

    this._isRedirecting = true;

    // Timer stoppen
    if (this.sessionTimer) clearTimeout(this.sessionTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);

    // Zeige Notification
    this.showNotification("Ihre Session ist abgelaufen. Sie werden zum Login weitergeleitet.");

    // Redirect nach kurzer Verzögerung, um sicherzustellen, dass die Notification sichtbar ist.
    setTimeout(() => {
      window.location.href = "/?session=expired";
    }, 3000);
  }

  /**
   * Notification anzeigen
   */
  showNotification(message) {
    if (typeof cs.Swal !== "undefined") {
      cs.Swal.fire({
        icon: "warning",
        title: "Session abgelaufen",
        text: message,
        timer: 3000,
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
    } else {
      alert(message);
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const isLoginPage = window.location.pathname === "/";

  // Nur initialisieren, wenn NICHT auf der Login-Seite.
  if (!isLoginPage) {
    window.sessionHandler = new SessionHandler();
    console.log("SessionHandler initialized");
  } else {
    console.log("SessionHandler nicht initialisiert (Login-Seite)");
  }
});
